import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { InkLaptop, InkClock, InkBriefcase, InkGrowth } from "./InkIcons";
import { useLang } from "../lib/lang";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";

const ICONS = [InkLaptop, InkClock, InkBriefcase, InkGrowth];
const TILT = ["-rotate-3", "rotate-2", "-rotate-2", "rotate-3"];

export const Benefits = () => {
  const { t } = useLang();
  return (
    <div className="relative z-10 mx-auto -mt-16 max-w-6xl px-5 sm:px-8" data-testid="benefit-strip">
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl bg-sky shadow-[0_30px_60px_-30px_rgba(18,24,63,0.45)] lg:grid-cols-4">
        {t.benefits.map((b, i) => {
          const Icon = ICONS[i];
          return (
            <motion.div key={b} initial={{ opacity: 0.001, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className="group flex flex-col items-start gap-4 bg-white p-5 sm:flex-row sm:items-center sm:p-7" data-testid={`benefit-${i}`}>
              <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-[1.1rem_1.4rem_1rem_1.5rem] bg-sky text-ink transition-[background-color,color,transform] duration-300 group-hover:rotate-0 group-hover:bg-ink group-hover:text-white ${TILT[i]}`}>
                <Icon className="h-8 w-8" />
              </span>
              <span className="font-display text-sm font-bold leading-snug text-ink sm:text-base">{b}</span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export const Suitable = () => {
  const { t } = useLang();
  return (
    <section className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-24 sm:px-8 lg:grid-cols-2 lg:py-32" data-testid="suitable-section">
      <Reveal className="relative order-2 lg:order-1">
        <div className="absolute -left-6 -top-6 h-40 w-40 rounded-full bg-sun" aria-hidden="true" />
        <div className="dot-grid-ink absolute -bottom-8 right-4 h-32 w-48 opacity-60" aria-hidden="true" />
        <div className="relative overflow-hidden rounded-[2rem] rounded-tl-[7rem]">
          <img src="/img/pro.webp" alt={t.suitable.alt} loading="lazy" width="700" height="1050" className="aspect-[4/5] w-full object-cover sm:aspect-[5/5]" />
        </div>
      </Reveal>
      <div className="order-1 lg:order-2">
        <Reveal><Eyebrow>{t.suitable.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.05}><SectionTitle>{t.suitable.title}</SectionTitle></Reveal>
        <Reveal delay={0.1}><p className="mt-6 max-w-lg text-base leading-relaxed text-night/80 sm:text-lg">{t.suitable.body}</p></Reveal>
        <ul className="mt-10 space-y-4">
          {t.suitable.items.map((it, i) => (
            <Reveal key={it} delay={0.12 + i * 0.07}>
              <li className="flex items-center gap-4 rounded-2xl border border-ink/10 bg-white p-4 sm:p-5" data-testid={`suitable-item-${i}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink text-sun"><Check className="h-5 w-5" strokeWidth={3} aria-hidden="true" /></span>
                <span className="font-display text-base font-bold text-night sm:text-lg">{it}</span>
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
};
